/**
 * 날짜 없는 메모 보드 — 현장 목록 페이지의 캘린더 아래.
 *
 * 왜 별도 컴포넌트인가: 캘린더는 보이는 기간(from/to)만 조회하는데, 시점 없는
 * 메모는 `scheduled_at IS NULL` 이라 어떤 기간 조회에도 걸리지 않는다. 즉 여기서
 * 메모를 등록해 놓고 이 화면을 못 찾으면 등록한 사람 눈에서 사라진다. 그래서
 * 기간 없이 한 번 더 조회해 메모만 따로 보여 준다.
 *
 * 표시는 최소한으로: 현장 · 제목 · 담당자 · 작성자. 자세한 내용은 카드를 눌러
 * 상세 다이얼로그에서 본다 (일정과 같은 다이얼로그 — 통일성).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  StickyNote2Outlined as MemoIcon,
} from '@mui/icons-material';

import AuthorChip from '../../../components/AuthorChip';
import ScheduleDetailDialog from '../../../pages/ProjectDashboardPage/dialogs/ScheduleDetailDialog';
import ScheduleFormDialog from '../../../components/dialogs/ScheduleFormDialog';
import { SiteScheduleService } from '../../../services/SiteScheduleService';
import { markdownToPlainText } from '../../../utils/markdown';
import { Project } from '../../../models/Project';
import { AllSiteSchedule, hasScheduleDate } from '../../../models/SiteSchedule';

export interface MemoBoardProps {
  /** 수정 시 현장을 바꿀 수 있게 넘긴다 — 전체 현장 화면이므로 고정 현장이 없다. */
  sites: Project[];
  /** admin/editor 만 수정/삭제 진입점을 본다. */
  writable: boolean;
  /**
   * 값이 바뀌면 다시 조회한다. 부모(캘린더)에서 메모를 등록한 직후 목록을
   * 갱신하기 위한 신호.
   */
  refreshKey?: number;
}

/** 한 번에 보여줄 최대 개수 — 그 이상은 현장 상세의 메모 목록에서 본다. */
const MAX_VISIBLE = 24;

const MemoBoard: React.FC<MemoBoardProps> = ({ sites, writable, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [memos, setMemos] = useState<AllSiteSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [detail, setDetail] = useState<AllSiteSchedule | null>(null);
  const [editing, setEditing] = useState<AllSiteSchedule | null>(null);

  const reload = useCallback(() => setLocalRefresh((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // 기간 인자를 주지 않는다 — scheduled_at 이 NULL 인 행까지 받아야 한다.
    SiteScheduleService.listAll()
      .then((list) => {
        if (cancelled) return;
        setMemos(list.filter((s) => !hasScheduleDate(s)));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '공지사항 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, localRefresh]);

  // 최근 작성 순 — 시점이 없으니 작성 시각만이 뜻 있는 순서다.
  const sorted = useMemo(
    () =>
      [...memos].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [memos],
  );

  // 메모가 없고 오류도 없으면 섹션 자체를 숨긴다 — 빈 박스는 화면만 먹는다.
  if (!loading && !error && sorted.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }} data-testid="memo-board">
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <MemoIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700}>
          날짜 없는 공지사항 {sorted.length > 0 && `(${sorted.length}건)`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          캘린더에는 뜨지 않습니다
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* 이 보드는 날짜 없는 공지사항만, 최근 것만 보여 준다 — 전부 보려면
            전체 공지사항 화면으로 넘어간다. */}
        <Button size="small" endIcon={<OpenInNewIcon fontSize="small" />} onClick={() => navigate('/memos')}>
          전체 공지사항
        </Button>
        <Button
          size="small"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={reload}
          disabled={loading}
        >
          새로고침
        </Button>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        {loading && sorted.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Stack divider={<Divider />}>
            {sorted.slice(0, MAX_VISIBLE).map((m) => (
              <Box
                key={m.id}
                role="button"
                tabIndex={0}
                onClick={() => setDetail(m)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDetail(m);
                  }
                }}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: -2,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.25, flexWrap: 'wrap', rowGap: 0.5 }}
                >
                  <Chip
                    label={m.site_name}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }}>
                    {m.title}
                  </Typography>
                </Stack>
                {m.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {markdownToPlainText(m.description)}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                >
                  {m.assignee && (
                    <Typography variant="caption" color="text.secondary">
                      담당 {m.assignee}
                    </Typography>
                  )}
                  {(m.created_by_name || m.created_by_id) && (
                    <AuthorChip name={m.created_by_name} userId={m.created_by_id} label="" dense />
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
        {sorted.length > MAX_VISIBLE && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 2, py: 1 }}
          >
            최근 {MAX_VISIBLE}건만 표시합니다. 나머지는 "전체 공지사항" 에서 보세요.
          </Typography>
        )}
      </Paper>

      <ScheduleDetailDialog
        open={!!detail}
        schedule={detail}
        onClose={() => setDetail(null)}
        onEdit={
          writable
            ? (s) => {
                setDetail(null);
                setEditing(s as AllSiteSchedule);
              }
            : undefined
        }
      />

      <ScheduleFormDialog
        open={!!editing}
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
        // 전체 현장 화면이라 고정 현장이 없다 — 수정 중 현장 이동도 허용.
        selectableSites={sites}
      />
    </Box>
  );
};

export default MemoBoard;
